const ISSUE_TOPIC_KEYS = new Set(['top-media-issue-topics', 'top-media-issue-topics-word-cloud']);
const PROACTIVE_TOPIC_KEYS = new Set(['top-proactive-media-topics', 'top-proactive-media-topics-word-cloud']);
const TYPE_KEYS = new Set(['media-interactions-by-type', 'services-by-type', 'engagements-by-type']);
const OVERALL_TOPIC_KEYS = new Set([
  'top-media-interaction-topics-overall',
  'top-media-interaction-topics-overall-word-cloud',
  'top-engagement-topics-overall',
]);
const COVERAGE_OUTLET_KEY = 'top-20-media-outlets';
const COVERAGE_EXPERTS_KEY = 'top-experts-word-cloud';
const TONE_KEY = 'interactions-by-tone';
const ENGAGEMENT_TOPIC_WORD_CLOUD_KEY = 'top-engagement-topics-word-cloud';
const PROACTIVE_VS_REACTIVE_KEY = 'proactive-vs-reactive-media-interactions';
const MEDIA_INTERACTIONS_PAST_YEARS_KEY = 'media-interactions-compared-to-past-years';

const normalizeQueryKey = (queryKey) => (typeof queryKey === 'string' ? queryKey.trim().toLowerCase() : '');
const normalizeCategory = (category) => (typeof category === 'string' ? category.trim().toLowerCase() : '');
const TOOLTIP_PROACTIVE = 'Includes entries with at least one Proactive Type of Work.';
const TOOLTIP_EXCLUSIVE = 'Entries tagged with both Proactive and Reactive Types of Work are excluded.';
const TOOLTIP_MULTI_TAG = 'Entries with multiple Types of Work are counted in each Type.';
const TOOLTIP_EXACT_MATCH = 'Topics are grouped exactly as entered (spelling, spacing, and capitalization matter).';
const TOOLTIP_NORMALIZED = 'Similar topics are grouped together by normalization; labels use the most common wording.';
const TOOLTIP_COVERAGE_GENERIC = 'Some entries may be excluded if required links/details are missing.';
const TOOLTIP_PERCENT = 'Displayed as a monthly percentage of the total.';
const TOOLTIP_NA_INCLUDED = 'Missing values appear as N/A.';
const TOOLTIP_ROLLING_WINDOW = 'Compares this selected date window to the same window in previous years.';
const TOOLTIP_PERCENT_CHANGE_WINDOW = 'Shows percent change vs previous years for the same date window.';
const COVERAGE_TOOLTIP_BY_QUERY_KEY = {
  [COVERAGE_OUTLET_KEY]:
    "Some interactions may be excluded if they can't be linked to an outlet through the journalist record.",
  [COVERAGE_EXPERTS_KEY]: 'Blank and TBD expert names are excluded.',
};

const shouldIncludeTopicGroupingBadge = (normalizedQueryKey, normalizedCategory) =>
  ISSUE_TOPIC_KEYS.has(normalizedQueryKey) ||
  OVERALL_TOPIC_KEYS.has(normalizedQueryKey) ||
  normalizedQueryKey === ENGAGEMENT_TOPIC_WORD_CLOUD_KEY ||
  normalizedQueryKey.includes('overall') ||
  normalizedCategory.includes('topics: overall');

const warnIfCoverageTooltipIsGeneric = (cappedBadges, queryKey) => {
  if (process.env.NODE_ENV === 'production' || !cappedBadges.some((badge) => badge.label === 'Coverage')) {
    return;
  }

  const hasSpecificCoverageTooltip = cappedBadges.some(
    (badge) =>
      badge.label === 'Coverage' &&
      badge.tooltip !== TOOLTIP_COVERAGE_GENERIC &&
      badge.tooltip !== TOOLTIP_NORMALIZED &&
      badge.tooltip !== TOOLTIP_EXACT_MATCH
  );
  if (!hasSpecificCoverageTooltip) {
    console.warn(`Coverage badge should use specific tooltip wording for chart: ${queryKey}`);
  }
};

const capRuleBadges = (badges, queryKey) => {
  const normalizedQueryKey = normalizeQueryKey(queryKey);
  const labels = new Set(badges.map((badge) => badge.label.toLowerCase()));
  const allowsThreeForProactiveExclusion =
    normalizedQueryKey === 'proactive-media-relations-tactics' &&
    labels.has('proactive') &&
    labels.has('exclusive') &&
    labels.has('excludes');
  const allowsThreeForYearlyCampaignNa =
    labels.has('yearly') && labels.has('campaign required') && labels.has('n/a included');
  const maxBadges = allowsThreeForProactiveExclusion || allowsThreeForYearlyCampaignNa ? 3 : 2;
  if (badges.length <= maxBadges) {
    return badges;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Capping custom analytics badges for ${queryKey} to ${maxBadges}.`);
  }
  return badges.slice(0, maxBadges);
};

const withUniqueBadges = (badges) => {
  const seen = new Set();
  const normalized = [];

  badges.forEach((badge) => {
    if (!badge || typeof badge.label !== 'string' || !badge.label.trim()) {
      return;
    }

    const normalizedBadge = {
      label: badge.label.trim(),
      variant: typeof badge.variant === 'string' ? badge.variant.trim().toLowerCase() : 'neutral',
      tooltip:
        typeof badge.tooltip === 'string' && badge.tooltip.trim()
          ? badge.tooltip.trim()
          : `${badge.label.trim()} badge`,
    };

    const dedupeKey = `${normalizedBadge.label.toLowerCase()}|${normalizedBadge.variant}`;
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      normalized.push(normalizedBadge);
    }
  });

  return normalized;
};

export const shouldHaveCustomAnalyticsBadges = (queryKey, category) => {
  const normalizedQueryKey = normalizeQueryKey(queryKey);
  const normalizedCategory = normalizeCategory(category);

  return (
    ISSUE_TOPIC_KEYS.has(normalizedQueryKey) ||
    PROACTIVE_TOPIC_KEYS.has(normalizedQueryKey) ||
    OVERALL_TOPIC_KEYS.has(normalizedQueryKey) ||
    TYPE_KEYS.has(normalizedQueryKey) ||
    normalizedQueryKey === COVERAGE_OUTLET_KEY ||
    normalizedQueryKey === COVERAGE_EXPERTS_KEY ||
    normalizedQueryKey === TONE_KEY ||
    normalizedQueryKey === MEDIA_INTERACTIONS_PAST_YEARS_KEY ||
    normalizedQueryKey === ENGAGEMENT_TOPIC_WORD_CLOUD_KEY ||
    normalizedQueryKey.includes('overall') ||
    normalizedCategory.includes('topics: overall')
  );
};

export const normalizeLegacyHeaderBadges = (headerBadges) => {
  if (!Array.isArray(headerBadges)) {
    return [];
  }

  return withUniqueBadges(
    headerBadges.map((badge) => ({
      label: badge,
      variant: 'neutral',
    }))
  );
};

export const getBadgesForCustomAnalytics = (queryKey, category, options = {}) => {
  const normalizedQueryKey = normalizeQueryKey(queryKey);
  const normalizedCategory = normalizeCategory(category); // retained for compatibility

  const badges = [];

  if (TYPE_KEYS.has(normalizedQueryKey)) {
    badges.push({
      label: 'Multi-tag',
      variant: 'neutral',
      tooltip: TOOLTIP_MULTI_TAG,
    });
  }

  if (shouldIncludeTopicGroupingBadge(normalizedQueryKey, normalizedCategory)) {
    badges.push({
      label: options?.topicGrouping === 'normalized' ? 'Normalized' : 'Exact match',
      variant: 'neutral',
      tooltip: options?.topicGrouping === 'normalized' ? TOOLTIP_NORMALIZED : TOOLTIP_EXACT_MATCH,
    });
  }

  if (PROACTIVE_TOPIC_KEYS.has(normalizedQueryKey)) {
    badges.push(
      {
        label: 'Proactive',
        variant: 'proactive',
        tooltip: TOOLTIP_PROACTIVE,
      },
      {
        label: 'Exclusive',
        variant: 'neutral',
        tooltip: TOOLTIP_EXCLUSIVE,
      }
    );
  }

  if (normalizedQueryKey === PROACTIVE_VS_REACTIVE_KEY && options?.isPercentageChart) {
    badges.push({
      label: 'Percent',
      variant: 'neutral',
      tooltip: TOOLTIP_PERCENT,
    });
  }

  const coverageTooltip = COVERAGE_TOOLTIP_BY_QUERY_KEY[normalizedQueryKey];
  if (coverageTooltip) {
    badges.push({
      label: 'Coverage',
      variant: 'neutral',
      tooltip: coverageTooltip,
    });
  }

  if (normalizedQueryKey === TONE_KEY) {
    badges.push({
      label: 'N/A included',
      variant: 'neutral',
      tooltip: TOOLTIP_NA_INCLUDED,
    });
  }

  if (normalizedQueryKey === MEDIA_INTERACTIONS_PAST_YEARS_KEY) {
    badges.push(
      {
        label: 'Rolling window',
        variant: 'neutral',
        tooltip: TOOLTIP_ROLLING_WINDOW,
      },
      {
        label: 'Percent change',
        variant: 'neutral',
        tooltip: TOOLTIP_PERCENT_CHANGE_WINDOW,
      }
    );
  }

  const normalizedBadges = withUniqueBadges(badges);
  const cappedBadges = capRuleBadges(normalizedBadges, queryKey);
  warnIfCoverageTooltipIsGeneric(cappedBadges, queryKey);
  return cappedBadges;
};
